import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
  createAdminUser,
  downloadAdminUsersCsv,
  getAdminUser,
  getAdminUsers,
  retryAdminSsoLink,
  updateAdminUser,
} from '@/services/admin/admins';
import type {
  AdminUserStorePayload,
  AdminUserUpdatePayload,
  AdminUsersListResponse,
  AdminUsersListParams,
} from '@/services/admin/admins/types';
import { getTenants } from '@/services/admin/tenants';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils';
import { CSV_ERROR_MESSAGE, CSV_SUCCESS_MESSAGE } from '@/constants/messages';
import { tenantCapability } from '@customize/registry';

export const pendingAdminSsoRefetchInterval = (
  payload: AdminUsersListResponse | undefined
): number | false => {
  // A refetch heuristic must never be able to crash the list when an older or
  // unexpected backend response has a different envelope shape.
  try {
    const rows = payload?.data?.data;

    return Array.isArray(rows) &&
      rows.some(
        row =>
          row.sso_link_status === 'pending' &&
          tenantCapability(row.manage_site, 'adminSsoMapping', true)
      )
      ? 3000
      : false;
  } catch {
    return false;
  }
};

export const useRootTenants = (site: string | null, enabled: boolean) => {
  return useQuery({
    queryKey: ['admin', site, 'tenants', 'root'],
    // Lọc hai lần là cố ý: server đã trả đúng tenant gốc, client giữ lại phép lọc để một
    // backend cũ (chưa có `roots_only`) không âm thầm thêm tenant con vào dropdown サイト区分.
    queryFn: () => getTenants(site!, true).then(list => list.filter(t => t.parent_id === null)),
    enabled: !!site && enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export const useAdminUsersList = (site: string | null, params: AdminUsersListParams) => {
  return useQuery({
    queryKey: ['admin', site, 'admins', params],
    queryFn: () => getAdminUsers(site!, params),
    enabled: !!site,
    staleTime: 0,
    // POLL ONLY WHILE SOMETHING IS ACTUALLY IN FLIGHT, then stop.
    //
    // Provisioning finishes on the queue AFTER the save response returns, so a list rendered
    // immediately after creating an admin shows `pending` and would sit there until the operator
    // reloaded by hand — which is exactly what QC reported ("button, then badge after reload").
    // Measured on dev1 2026-09-04: `unlinked` at t+4s, `linked` at t+10s. A few seconds of
    // polling covers that window; polling unconditionally would put every open list on a timer
    // forever for a state that is normally absent.
    refetchInterval: query => pendingAdminSsoRefetchInterval(query.state.data),
  });
};

export const useAdminUserCreate = (site: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: AdminUserStorePayload) => createAdminUser(site!, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', site, 'admins'] });
    },
  });
};

export const useAdminUserDetail = (site: string | null, id: number | null) => {
  return useQuery({
    queryKey: ['admin', site, 'admins', id],
    queryFn: () => getAdminUser(site!, id!),
    enabled: !!site && !!id,
    staleTime: 0,
  });
};

export const useAdminUserUpdate = (site: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AdminUserUpdatePayload }) =>
      updateAdminUser(site!, id, payload),
    meta: { suppressNotFoundToast: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', site, 'admins'] });
    },
  });
};

/**
 * Nút 「SSO連携」 ở màn list (BD sheet 2, mục 7.0).
 *
 * Không tự hiện toast ở đây: màn hình cần phân biệt 422 「thiếu mật khẩu」 (phải mở ô nhập) với
 * lỗi hạ tầng (chỉ báo). Phần đó nằm ở AdminUserListPage.
 */
export const useAdminUserSsoLinkRetry = (site: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: number; password?: string }) =>
      retryAdminSsoLink(site!, id, password),
    // `suppressServerErrorModal` KHÔNG phải để cho gọn.
    //
    // Endpoint này trả 502 khi Cognito hỏng. Nếu không tắt, `handleServerError` (shared/lib/
    // react-query) sẽ bắn SERVER_ERROR_EVENT; `useAppToast` bật `globalServerErrorSuppressed`
    // trong 500ms và NUỐT LUÔN toast mà onError của màn hình vừa gọi — người dùng chỉ còn thấy
    // modal lỗi chung 「サーバーエラー」, không bao giờ thấy 「SSO連携に失敗しました。」.
    //
    // 502 ở đây không phải "server hỏng" mà là một thất bại cụ thể, có việc để làm tiếp (thử
    // lại sau, hoặc nhập mật khẩu), nên thông điệp riêng có ích hơn modal chung.
    meta: { suppressNotFoundToast: true, suppressServerErrorModal: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', site, 'admins'] });
    },
  });
};

export const useAdminUsersCsv = () => {
  const { site } = useParams<{ site: string }>();
  const siteType = site ?? '';
  const toast = useAppToast();

  return useMutation({
    mutationFn: (params?: AdminUsersListParams) => downloadAdminUsersCsv(siteType, params ?? {}),
    onSuccess: () => {
      toast.success(CSV_SUCCESS_MESSAGE);
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, CSV_ERROR_MESSAGE));
    },
  });
};
